import React, { useEffect, useState } from "react";
import axios from "../../axios/axios";
import Layout from "./Layout";
import { ToastContainer, toast } from "react-toastify";
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";
import PaginationControls from "../../Components/PaginationControls";
import RowsPerPageSelect from "../../Components/RowsPerPageSelect";
import ExportCsvButton from "../../Components/ExportCsvButton";
import DateRangeFilter from "../../Components/DateRangeFilter";

const UserOrders = () => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [statusFilter, setStatusFilter] = useState(0); // 0 means no filter
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const apiEndpoints = [
        "/retouching/",
        "/real-estate/",
        "/color-correction/",
        "/culling/",
        "/video-editing/",
        "/wedding-album/",
      ];
      const responses = await Promise.all(
        apiEndpoints.map((endpoint) => axios.get(endpoint))
      );
      const mergedOrders = responses.flatMap((response) => response.data);
      const updatedOrders = mergedOrders.map((order) => {
        order.paymentStatus =
          order.paymentStatus === "paid" ? "Paid" : "Unpaid";
        return order;
      });
      setOrders(updatedOrders);
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  const getStatusDetails = (status) => {
    switch (status) {
      case 1:
        return { text: "New Order", className: " bg-primary text-light" };
      case 2:
        return { text: "In Progress", className: "bg-warning text-light" };
      case 3:
        return { text: "On Hold", className: "bg-secondary text-light" };
      case 4:
        return { text: "In QC", className: "bg-info text-dark" };
      case 5:
        return { text: "Sample Sent", className: "bg-success text-light" };
      case 6:
        return { text: "In Revision", className: "bg-info text-light" };
      case 7:
        return { text: "Cancelled", className: "bg-danger text-light" };
      case 8:
        return { text: "Completed", className: "bg-success text-light" };
      default:
        return { text: "Unknown", className: "bg-dark text-light" };
    }
  };

  const filteredOrders = orders.filter((val) => {
    if (statusFilter !== 0 && val.status !== statusFilter) return false;
    const createdAt = new Date(val.createdAt);
    if (startDate && createdAt < new Date(startDate)) return false;
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (createdAt > end) return false;
    }
    return true;
  });

  const indexOfLastOrder = currentPage * rowsPerPage;
  const indexOfFirstOrder = indexOfLastOrder - rowsPerPage;
  const currentOrders = filteredOrders.slice(
    indexOfFirstOrder,
    indexOfLastOrder
  );
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const fetchAllForExport = async () => filteredOrders;

  const csvColumns = [
    { label: "User ID", accessor: (row) => row?.userId?.id },
    { label: "Order ID", accessor: (row) => row.id },
    { label: "Order Type", accessor: (row) => row.serviceId?.service },
    { label: "Event Name", accessor: (row) => row.order_name },
    { label: "Total Image", accessor: (row) => row.quantity },
    { label: "Price", accessor: (row) => row.price },
    {
      label: "Date",
      accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      label: "Order Status",
      accessor: (row) => getStatusDetails(row.status).text,
    },
    { label: "Editor", accessor: (row) => row.editor },
  ];

  return (
    <>
      <Navbar />
      <Layout>
        <div className=" px-4 bg-light py-5">
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <div className="my-4 col-md-3 mx-auto">
            <label htmlFor="statusFilter" className="form-label fw-bold">
              Filter by Status:
            </label>
            <select
              id="statusFilter"
              className="form-select"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(Number(e.target.value))}
            >
              <option value={0}>All</option>
              <option value={1}>New Order</option>
              <option value={2}>In Progress</option>
              <option value={3}>On Hold</option>
              <option value={4}>In QC</option>
              <option value={5}>Sample Sent</option>
              <option value={6}>In Revision</option>
              <option value={7}>Cancelled</option>
              <option value={8}>Completed</option>
            </select>
          </div>
          <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              onClear={handleClearDates}
            />
            <div className="d-flex align-items-center gap-3">
              <ExportCsvButton
                filename="all-orders.csv"
                columns={csvColumns}
                fetchRows={fetchAllForExport}
              />
              <RowsPerPageSelect
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </div>
          </div>
          <div
            style={{ width: "100%", overflowX: "auto" }}
            className="scrollable-table "
          >
            <table className="table table-container table-light">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User ID</th>
                  <th>Order ID</th>
                  <th>Order Type</th>
                  <th>Event Name</th>
                  <th>Total Image</th>
                  <th>Price</th>
                  <th>Date</th>
                  <th>Order Status</th>
                  <th>Editor</th>
                </tr>
              </thead>
              <tbody>
                {currentOrders.map((val, index) => {
                  const { text, className } = getStatusDetails(val.status);
                  return (
                    <tr key={index}>
                      <th scope="row">{indexOfFirstOrder + index + 1}</th>
                      <td>{val?.userId?.id}</td>
                      <td>{val.id}</td>
                      <td>{val.serviceId?.service}</td>
                      <td>{val.order_name}</td>
                      <td>{val.quantity}</td>
                      <td>${val.price}</td>
                      <td>{new Date(val.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          className={`btn text-center p-2 rounded ${className}`}
                        >
                          {text}
                        </button>
                      </td>
                      <td>{val.editor}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={filteredOrders.length}
            onPageChange={(page) =>
              setCurrentPage(Math.min(Math.max(page, 1), totalPages || 1))
            }
          />
        </div>
      </Layout>
      <Footer />
    </>
  );
};

export default UserOrders;
